import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  InputAdornment,
  IconButton,
  Chip,
  Autocomplete,
  Divider,
  OutlinedInput,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility,
  VisibilityOff,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import {
  VENDOR_META,
  CREDENTIAL_FIELD_META,
  CAPABILITY_LABELS,
  SEVERITY_OPTIONS,
  describeApiError,
} from '../Integrations/agentMeta';

const defaultSettings = () => ({
  ingestion: {
    enabled: true,
    intervalMinutes: 15,
    lookbackHours: 24,
    maxAlertsPerRun: 100,
    severityFilter: [],
    statusFilter: [],
    aiClassification: true,
    importVendorStatus: false,
    resolveWhenVendorCloses: false,
  },
  enrichment: {
    enableDetectionIngestion: true,
    enableEndpointEnrichment: true,
    enableThreatIntelligence: true,
    autoEnrichOnDetectionCreate: true,
    autoEnrichOnCaseCreate: true,
    cacheResponsesHours: 24,
  },
  filters: {
    severityLevels: [],
    categories: [],
    enrichOnlyRecentDetections: true,
    maxDetectionAgeHours: 72,
  },
  rateLimits: {
    requestsPerMinute: 60,
    requestsPerHour: 3600,
    concurrency: 4,
  },
  sync: {
    pushStatusToVendor: false,
  },
});

const emptyCredentials = () => ({
  clientId: '',
  clientSecret: '',
  tenantId: '',
  apiToken: '',
  memberCid: '',
  apiEndpoint: '',
});

const buildFormFromAgent = (agent) => {
  const defaults = defaultSettings();
  if (!agent) {
    return { name: '', type: '', description: '', isEnabled: true, credentials: emptyCredentials(), settings: defaults };
  }
  const s = agent.settings || {};
  const merge = (section) => ({ ...defaults[section], ...(s[section] || {}) });
  return {
    name: agent.name || '',
    type: agent.type || '',
    description: agent.description || '',
    isEnabled: agent.isEnabled !== false,
    credentials: { ...emptyCredentials(), apiEndpoint: (agent.credentials && agent.credentials.apiEndpoint) || '' },
    settings: {
      ingestion: merge('ingestion'),
      enrichment: merge('enrichment'),
      filters: merge('filters'),
      rateLimits: merge('rateLimits'),
    },
  };
};

const toInt = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? '' : n;
};

let cachedTypes = null;

/**
 * Create/edit dialog for any vendor agent. Fields are driven by GET /agents/types.
 * Props: open, onClose(shouldReload), agent (null for create).
 */
const AgentConfigurationDialog = ({ open, onClose, agent }) => {
  const [types, setTypes] = useState(cachedTypes || []);
  const [typesLoading, setTypesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState(buildFormFromAgent(null));

  useEffect(() => {
    if (!open) return;
    setFormData(buildFormFromAgent(agent));
    setError(null);
    setFieldErrors({});
    setTestResult(null);
    setShowPasswords({});
    if (!cachedTypes) {
      setTypesLoading(true);
      apiService.agents.getTypes()
        .then((res) => {
          cachedTypes = res.data.types || [];
          setTypes(cachedTypes);
        })
        .catch((err) => setError(describeApiError(err, 'Failed to load integration types').message))
        .finally(() => setTypesLoading(false));
    }
  }, [agent, open]);

  const selectedType = useMemo(() => types.find((t) => t.type === formData.type) || null, [types, formData.type]);
  const vendor = VENDOR_META[formData.type] || {};
  const credentialFields = selectedType ? selectedType.credentialFields : [];
  const masked = (agent && agent.credentials) || {};

  const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const setCredential = (field, value) => setFormData((prev) => ({ ...prev, credentials: { ...prev.credentials, [field]: value } }));
  const setSetting = (section, field, value) => setFormData((prev) => ({
    ...prev,
    settings: { ...prev.settings, [section]: { ...prev.settings[section], [field]: value } },
  }));

  const isConfigured = (field) => {
    const meta = CREDENTIAL_FIELD_META[field];
    return !!(agent && meta && meta.maskedKey && masked[meta.maskedKey]);
  };

  // Vendor-specific overrides: some vendors make optional fields required (Cortex key ID + URL) or required fields optional (Sophos tenant).
  const fieldOptional = (field) => {
    const meta = CREDENTIAL_FIELD_META[field] || {};
    if (vendor.requiredOverride && vendor.requiredOverride[field]) return false;
    if (vendor.optionalOverride && vendor.optionalOverride[field]) return true;
    return !!meta.optional;
  };
  const endpointRequired = formData.type === 'sentinelone' || !!(vendor.requiredOverride && vendor.requiredOverride.apiEndpoint);
  const endpointConfigured = !!(agent && agent.credentials && agent.credentials.apiEndpoint);

  const missingRequired = credentialFields.filter((field) => {
    if (field === 'apiEndpoint') return false;
    if (fieldOptional(field)) return false;
    const typed = (formData.credentials[field] || '').trim() !== '';
    return !typed && !isConfigured(field);
  });
  const needsEndpoint = endpointRequired && !(formData.credentials.apiEndpoint || '').trim() && !endpointConfigured;

  const canSubmit = formData.name.trim() !== '' && formData.type !== '' && missingRequired.length === 0 && !needsEndpoint;

  const buildPayload = () => {
    const credentials = {};
    credentialFields.forEach((field) => {
      const value = (formData.credentials[field] || '').trim();
      if (value) credentials[field] = value; // blank = keep existing (server semantics)
    });
    const s = formData.settings;
    const num = (v, fallback) => (v === '' || v === null || v === undefined ? fallback : Number(v));
    return {
      name: formData.name.trim(),
      type: formData.type,
      description: formData.description,
      isEnabled: formData.isEnabled,
      credentials,
      settings: {
        ingestion: {
          ...s.ingestion,
          intervalMinutes: num(s.ingestion.intervalMinutes, 15),
          lookbackHours: num(s.ingestion.lookbackHours, 24),
          maxAlertsPerRun: num(s.ingestion.maxAlertsPerRun, 100),
        },
        enrichment: { ...s.enrichment, cacheResponsesHours: num(s.enrichment.cacheResponsesHours, 24) },
        filters: { ...s.filters, maxDetectionAgeHours: num(s.filters.maxDetectionAgeHours, 72) },
        rateLimits: {
          requestsPerMinute: num(s.rateLimits.requestsPerMinute, 60),
          requestsPerHour: num(s.rateLimits.requestsPerHour, 3600),
          concurrency: num(s.rateLimits.concurrency, 4),
        },
        sync: { pushStatusToVendor: !!(s.sync && s.sync.pushStatusToVendor) },
      },
    };
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});
      const payload = buildPayload();
      if (agent) {
        await apiService.agents.update(agent.id || agent._id, payload);
      } else {
        await apiService.agents.create(payload);
      }
      onClose(true);
    } catch (err) {
      const described = describeApiError(err, 'Failed to save agent configuration');
      setError(described.message);
      setFieldErrors(described.fieldErrors);
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!agent) return;
    try {
      setTesting(true);
      setTestResult(null);
      const res = await apiService.agents.testConnection(agent.id || agent._id);
      setTestResult({
        success: !!res.data.success,
        message: res.data.success ? res.data.message || 'Connection successful' : res.data.error || res.data.message || 'Connection failed',
        details: res.data.details,
      });
    } catch (err) {
      setTestResult({ success: false, message: describeApiError(err, 'Connection test failed').message });
    } finally {
      setTesting(false);
    }
  };

  const errorFor = (path) => fieldErrors[path];

  const renderCredentialField = (field) => {
    const baseMeta = CREDENTIAL_FIELD_META[field] || { label: field };
    const serverLabel = selectedType && selectedType.credentialLabels && selectedType.credentialLabels[field];
    const meta = { ...baseMeta, label: (vendor.credentialLabels && vendor.credentialLabels[field]) || serverLabel || baseMeta.label, optional: fieldOptional(field) };
    const configured = isConfigured(field);
    const isEndpoint = field === 'apiEndpoint';
    const isSecret = !!meta.password;
    let helper = errorFor(`credentials.${field}`);
    if (!helper) {
      if (isEndpoint) helper = vendor.endpointHelp;
      else if (configured) helper = 'Configured. Leave blank to keep the existing value.';
      else if (meta.optional) helper = 'Optional';
    }
    let placeholder;
    if (isEndpoint) placeholder = vendor.endpointPlaceholder;
    else if (configured) placeholder = isSecret ? '••••••••  (configured)' : 'configured';

    return (
      <TextField
        key={field}
        fullWidth
        margin="normal"
        label={meta.label}
        type={isSecret && !showPasswords[field] ? 'password' : 'text'}
        value={formData.credentials[field] || ''}
        onChange={(e) => setCredential(field, e.target.value)}
        required={isEndpoint ? endpointRequired && !endpointConfigured : !meta.optional && !configured}
        placeholder={placeholder}
        helperText={helper}
        error={!!errorFor(`credentials.${field}`)}
        autoComplete="off"
        InputProps={isSecret ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPasswords((p) => ({ ...p, [field]: !p[field] }))} edge="end" size="small">
                {showPasswords[field] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
      />
    );
  };

  const renderMultiSeverity = (label, value, onChange, helperText) => (
    <FormControl fullWidth margin="normal">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value || []}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((v) => <Chip key={v} label={v} size="small" />)}
          </Box>
        )}
      >
        {SEVERITY_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </Select>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, mt: 0.5 }}>{helperText}</Typography>
    </FormControl>
  );

  const renderChipInput = (label, value, onChange, helperText) => (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value || []}
      onChange={(e, newValue) => onChange(newValue.map((v) => String(v).trim()).filter(Boolean))}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
      ))}
      renderInput={(params) => (
        <TextField {...params} label={label} margin="normal" helperText={helperText} placeholder="Type and press Enter" />
      )}
    />
  );

  const capabilities = (selectedType && selectedType.capabilities) || {};

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{agent ? `Edit integration: ${agent.name}` : 'Add integration'}</DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Basic information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" required disabled={!!agent || typesLoading} error={!!errorFor('type')}>
              <InputLabel>Vendor</InputLabel>
              <Select value={formData.type} label="Vendor" onChange={(e) => setField('type', e.target.value)}>
                {types.map((t) => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
              </Select>
              {typesLoading && <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>Loading vendors…</Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              required
              label="Name"
              value={formData.name}
              onChange={(e) => setField('name', e.target.value)}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'A descriptive name, e.g. "Falcon – Production"'}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="dense"
              label="Description"
              value={formData.description}
              onChange={(e) => setField('description', e.target.value)}
              multiline
              minRows={2}
              error={!!errorFor('description')}
              helperText={errorFor('description')}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.isEnabled} onChange={(e) => setField('isEnabled', e.target.checked)} />}
              label="Enabled"
            />
          </Grid>
        </Grid>

        {selectedType && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Capabilities: </Typography>
            {Object.entries(capabilities).filter(([, v]) => v).map(([k]) => (
              <Chip key={k} size="small" label={CAPABILITY_LABELS[k] || k} sx={{ mr: 0.5 }} />
            ))}
            {vendor.setupHint && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{vendor.setupHint}</Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Credentials</Typography>
        {!selectedType ? (
          <Alert severity="info" sx={{ mt: 1 }}>Select a vendor to see the required credentials.</Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Credentials are encrypted at rest. {agent ? 'Only fields you type into are updated.' : ''}
            </Typography>
            {credentialFields.map(renderCredentialField)}
            {needsEndpoint && (
              <Typography variant="caption" color="error">{vendor.shortLabel || 'This vendor'} requires the API URL.</Typography>
            )}
            {agent && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
                  onClick={handleTest}
                  disabled={testing}
                >
                  Test connection
                </Button>
                <Typography variant="caption" color="text.secondary">Tests the saved credentials (save first to test new values).</Typography>
              </Box>
            )}
            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1 }} onClose={() => setTestResult(null)}>
                {testResult.message}
                {testResult.details && (
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {typeof testResult.details === 'string' ? testResult.details : JSON.stringify(testResult.details, null, 2)}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Settings</Typography>

        <Accordion defaultExpanded={!agent} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Alert ingestion</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {selectedType && !capabilities.ingestion && (
              <Alert severity="info" sx={{ mb: 2 }}>This vendor does not support alert ingestion.</Alert>
            )}
            <FormControlLabel
              control={<Switch checked={!!formData.settings.ingestion.enabled} onChange={(e) => setSetting('ingestion', 'enabled', e.target.checked)} />}
              label="Scheduled ingestion enabled"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth margin="normal" type="number" label="Interval (minutes)"
                  value={formData.settings.ingestion.intervalMinutes}
                  onChange={(e) => setSetting('ingestion', 'intervalMinutes', toInt(e.target.value))}
                  inputProps={{ min: 5, max: 1440 }} helperText="5 – 1440"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth margin="normal" type="number" label="Lookback (hours)"
                  value={formData.settings.ingestion.lookbackHours}
                  onChange={(e) => setSetting('ingestion', 'lookbackHours', toInt(e.target.value))}
                  inputProps={{ min: 1, max: 720 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth margin="normal" type="number" label="Max alerts per run"
                  value={formData.settings.ingestion.maxAlertsPerRun}
                  onChange={(e) => setSetting('ingestion', 'maxAlertsPerRun', toInt(e.target.value))}
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>
            </Grid>
            {renderMultiSeverity('Severity filter', formData.settings.ingestion.severityFilter, (v) => setSetting('ingestion', 'severityFilter', v), 'Only ingest alerts with these severities (empty = all).')}
            {renderChipInput('Vendor status filter', formData.settings.ingestion.statusFilter, (v) => setSetting('ingestion', 'statusFilter', v), 'Vendor status values to ingest, e.g. new, in_progress (empty = vendor default).')}
            <FormControlLabel
              control={<Switch checked={!!formData.settings.ingestion.aiClassification} onChange={(e) => setSetting('ingestion', 'aiClassification', e.target.checked)} />}
              label="AI classification of ingested alerts"
            />
            <FormControlLabel
              control={<Switch checked={!!formData.settings.ingestion.importVendorStatus} onChange={(e) => setSetting('ingestion', 'importVendorStatus', e.target.checked)} />}
              label="Import the vendor's workflow status as the detection status"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1, mb: 1 }}>
              Off by default: new detections always start as New in Excubia; the vendor's own status is still recorded for reference.
            </Typography>
            <FormControlLabel
              control={<Switch checked={!!formData.settings.ingestion.resolveWhenVendorCloses} onChange={(e) => setSetting('ingestion', 'resolveWhenVendorCloses', e.target.checked)} />}
              label="Resolve detections automatically when the vendor closes the alert"
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Enrichment</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {[
                ['enableDetectionIngestion', 'Enable detection ingestion'],
                ['enableEndpointEnrichment', 'Enable endpoint enrichment'],
                ['enableThreatIntelligence', 'Enable threat intelligence lookups'],
                ['autoEnrichOnDetectionCreate', 'Auto-enrich when a detection is created'],
                ['autoEnrichOnCaseCreate', 'Auto-enrich when a case is created'],
              ].map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={<Switch checked={formData.settings.enrichment[key] !== false} onChange={(e) => setSetting('enrichment', key, e.target.checked)} />}
                  label={label}
                />
              ))}
            </Box>
            <TextField
              fullWidth margin="normal" type="number" label="Cache responses (hours)"
              value={formData.settings.enrichment.cacheResponsesHours}
              onChange={(e) => setSetting('enrichment', 'cacheResponsesHours', toInt(e.target.value))}
              inputProps={{ min: 1, max: 168 }} helperText="1 – 168 hours"
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Only auto-enrich detections matching…</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">Leave a filter empty to apply no restriction.</Typography>
            {renderMultiSeverity('Severity levels', formData.settings.filters.severityLevels, (v) => setSetting('filters', 'severityLevels', v), 'Empty = all severities.')}
            {renderChipInput('Categories', formData.settings.filters.categories, (v) => setSetting('filters', 'categories', v), 'Detection categories, e.g. Endpoint, Network (empty = all).')}
            <FormControlLabel
              control={<Switch checked={formData.settings.filters.enrichOnlyRecentDetections !== false} onChange={(e) => setSetting('filters', 'enrichOnlyRecentDetections', e.target.checked)} />}
              label="Only recent detections"
            />
            <TextField
              fullWidth margin="normal" type="number" label="Max detection age (hours)"
              value={formData.settings.filters.maxDetectionAgeHours}
              onChange={(e) => setSetting('filters', 'maxDetectionAgeHours', toInt(e.target.value))}
              inputProps={{ min: 1, max: 8760 }}
              disabled={formData.settings.filters.enrichOnlyRecentDetections === false}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Alert status sync</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={<Switch checked={!!(formData.settings.sync && formData.settings.sync.pushStatusToVendor)} onChange={(e) => setSetting('sync', 'pushStatusToVendor', e.target.checked)} disabled={!!selectedType && selectedType.capabilities && selectedType.capabilities.alertStatus === false} />}
              label={`Push Excubia status changes to ${vendor.shortLabel || 'the vendor'}`}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
              Off by default. When on, setting a detection to In Progress, Resolved, False Positive or Closed (including bulk updates) updates the alert in the vendor console. The manual "Push status" button on a detection works regardless.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Rate limits</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth type="number" label="Requests per minute"
                  value={formData.settings.rateLimits.requestsPerMinute}
                  onChange={(e) => setSetting('rateLimits', 'requestsPerMinute', toInt(e.target.value))}
                  inputProps={{ min: 1, max: 1000 }}
                  error={!!errorFor('settings.rateLimits.requestsPerMinute')}
                  helperText={errorFor('settings.rateLimits.requestsPerMinute')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth type="number" label="Requests per hour"
                  value={formData.settings.rateLimits.requestsPerHour}
                  onChange={(e) => setSetting('rateLimits', 'requestsPerHour', toInt(e.target.value))}
                  inputProps={{ min: 1, max: 100000 }}
                  error={!!errorFor('settings.rateLimits.requestsPerHour')}
                  helperText={errorFor('settings.rateLimits.requestsPerHour')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth type="number" label="Concurrent requests"
                  value={formData.settings.rateLimits.concurrency}
                  onChange={(e) => setSetting('rateLimits', 'concurrency', toInt(e.target.value))}
                  inputProps={{ min: 1, max: 20 }}
                  error={!!errorFor('settings.rateLimits.concurrency')}
                  helperText={errorFor('settings.rateLimits.concurrency') || '1 – 20 (default 4)'}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !canSubmit}>
          {loading ? <CircularProgress size={20} /> : agent ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentConfigurationDialog;
